import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { ArrowLeft, Award, CheckCircle2, Clock, FileText, Loader2, ShieldCheck, Smartphone } from 'lucide-react';
import { Card, Button } from '../components';
import { getDocumenten, vraagSmsCode, ondertekenDocument, downloadCertificaat, documentBestandUrl } from '../services/medewerkerApi';
import type { PoortwachterDocument } from '../services/medewerkerApi';

/**
 * Medewerker-cockpit — Digital Signing Wrapper (stap 2 + 3 van de flow).
 *
 * Split-screen: links het document (tekst of geüploade PDF), rechts de
 * interactieve akkoordverklaring. Vinkje → [✍️ Onderteken digitaal] → OTP per
 * SMS naar het geverifieerde nummer → zegel + Audit-Certificaat.
 * Geen app nodig; werkt in elke mobiele of desktopbrowser.
 */

function foutMelding(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const msg = (error.response?.data as { message?: string } | undefined)?.message;
    if (msg) return msg;
  }
  return fallback;
}

function datum(iso: string): string {
  return new Date(iso).toLocaleString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const inputClass =
  'w-full rounded-lg border border-gray-300 px-3 py-3 text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#0B1F3A] focus:border-[#0B1F3A]';

// ─── Stap 3: SMS-2FA verzegeling ──────────────────────────────────────────────

const Ondertekenen: React.FC<{ doc: PoortwachterDocument; onKlaar: () => void }> = ({ doc, onKlaar }) => {
  const { t } = useTranslation('medewerker');
  const [akkoord, setAkkoord] = useState(false);
  const [stap, setStap] = useState<'akkoord' | 'telefoon' | 'code'>('akkoord');
  const [telefoon, setTelefoon] = useState('');
  const [suffix, setSuffix] = useState<string | null>(doc.telefoon_suffix_bekend);
  const [code, setCode] = useState('');

  const smsMutation = useMutation({
    mutationFn: (nummer?: string) => vraagSmsCode(doc.id, nummer),
    onSuccess: (res) => {
      setSuffix(res.telefoon_suffix);
      setStap('code');
    },
  });
  const ondertekenMutation = useMutation({
    mutationFn: () => ondertekenDocument(doc.id, code.trim()),
    onSuccess: onKlaar,
  });

  // Klik op de primaire knop: bekend nummer → OTP direct; anders eerst het nummer vragen
  const start = () => {
    if (doc.telefoon_bekend) smsMutation.mutate(undefined);
    else setStap('telefoon');
  };

  return (
    <div className="space-y-3">
      <h2 className="font-semibold text-gray-900">{t('documenten.akkoordTitel', { defaultValue: 'Akkoordverklaring' })}</h2>
      <label className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-gray-800 cursor-pointer">
        <input type="checkbox" className="mt-1 h-5 w-5" checked={akkoord} onChange={(e) => setAkkoord(e.target.checked)} disabled={stap === 'code'} />
        <span>{doc.akkoordverklaring}</span>
      </label>

      {stap === 'akkoord' && (
        <Button size="lg" className={`w-full transition ${akkoord ? 'ring-4 ring-amber-300' : ''}`} disabled={!akkoord} loading={smsMutation.isPending} onClick={start}>
          ✍️ {t('documenten.ondertekenDigitaal', { defaultValue: 'Onderteken digitaal' })}
        </Button>
      )}
      {smsMutation.isError && stap !== 'telefoon' && (
        <p className="text-sm text-red-600" role="alert">{foutMelding(smsMutation.error, t('documenten.smsMislukt', { defaultValue: 'De SMS kon niet worden verstuurd.' }))}</p>
      )}

      {stap === 'telefoon' && (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (telefoon.trim()) smsMutation.mutate(telefoon.trim());
          }}
        >
          <label htmlFor={`tel-${doc.id}`} className="block text-sm text-gray-800">{t('documenten.telefoonLabel', { defaultValue: 'Je mobiele nummer' })}</label>
          <input id={`tel-${doc.id}`} type="tel" inputMode="tel" autoComplete="tel" placeholder="06 12345678" value={telefoon} onChange={(e) => setTelefoon(e.target.value)} className={inputClass} required />
          {smsMutation.isError && <p className="text-sm text-red-600" role="alert">{foutMelding(smsMutation.error, t('documenten.smsMislukt', { defaultValue: 'De SMS kon niet worden verstuurd.' }))}</p>}
          <Button type="submit" size="lg" className="w-full" disabled={!telefoon.trim()} loading={smsMutation.isPending}>
            {t('documenten.stuurCode', { defaultValue: 'Stuur mij een SMS-code' })}
          </Button>
        </form>
      )}

      {stap === 'code' && (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (code.trim().length === 6) ondertekenMutation.mutate();
          }}
        >
          <p className="text-sm text-gray-700 flex items-center gap-1.5">
            <Smartphone className="w-4 h-4 text-amber-700" aria-hidden="true" />
            {t('documenten.codeVerstuurd', { defaultValue: 'We hebben een 6-cijferige code gestuurd naar •••• {{suffix}}.', suffix: suffix ?? '' })}
          </p>
          <label htmlFor={`code-${doc.id}`} className="block text-sm text-gray-800">{t('documenten.codeLabel', { defaultValue: 'SMS-code' })}</label>
          <input
            id={`code-${doc.id}`}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className={`${inputClass} text-center tracking-[0.5em] font-mono text-xl`}
            required
            autoFocus
          />
          {ondertekenMutation.isError && (
            <p className="text-sm text-red-600" role="alert">{foutMelding(ondertekenMutation.error, t('documenten.ondertekenMislukt', { defaultValue: 'Ondertekenen is niet gelukt. Controleer de code.' }))}</p>
          )}
          <Button type="submit" size="lg" className="w-full" disabled={code.length !== 6} loading={ondertekenMutation.isPending}>
            {t('documenten.onderteken', { defaultValue: 'Akkoord en ondertekenen' })}
          </Button>
          <button
            type="button"
            onClick={() => {
              setStap('telefoon');
              setCode('');
              smsMutation.reset();
            }}
            className="w-full text-sm text-[#0B1F3A] underline"
          >
            {t('documenten.anderNummer', { defaultValue: 'Ander nummer of code opnieuw sturen' })}
          </button>
        </form>
      )}
    </div>
  );
};

// ─── Documentkaart (split-screen) ─────────────────────────────────────────────

const DocumentKaart: React.FC<{ doc: PoortwachterDocument; onGewijzigd: () => void; focus: boolean }> = ({ doc, onGewijzigd, focus }) => {
  const { t } = useTranslation('medewerker');
  const [open, setOpen] = useState(doc.status !== 'ondertekend' || focus);
  const [zojuist, setZojuist] = useState(false);
  const [downloadt, setDownloadt] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focus) ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [focus]);

  const verzegeld = doc.status === 'ondertekend' || zojuist;
  const teTekenen = doc.status === 'werkgever_getekend' && !zojuist;
  const wachtOpWerkgever = doc.status === 'ter_ondertekening';

  const statusChip = verzegeld ? (
    <span className="inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 text-green-800 px-2 py-0.5 text-xs font-medium">
      <CheckCircle2 className="w-3 h-3" aria-hidden="true" /> {t('documenten.statusOndertekend', { defaultValue: 'Verzegeld' })}
    </span>
  ) : teTekenen ? (
    <span className="inline-flex items-center rounded-full border border-orange-300 bg-orange-50 text-orange-800 px-2 py-0.5 text-xs font-medium">
      🟠 {t('documenten.statusTeOndertekenen', { defaultValue: 'Te ondertekenen' })}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 text-gray-700 px-2 py-0.5 text-xs font-medium">
      <Clock className="w-3 h-3" aria-hidden="true" /> {t('documenten.statusWachtWerkgever', { defaultValue: 'Wacht op werkgever' })}
    </span>
  );

  const certificaat = async () => {
    setDownloadt(true);
    try {
      await downloadCertificaat(doc);
    } finally {
      setDownloadt(false);
    }
  };

  return (
    <div ref={ref}>
      <Card className={`p-4 ${focus ? 'ring-2 ring-[#0B1F3A]' : ''}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-xs text-gray-500">{doc.soort_label}</p>
            <h2 className="font-semibold text-gray-900 leading-tight">{doc.titel}</h2>
            <p className="text-xs text-gray-600 mt-1">
              {doc.bron === 'werkgever_formulier' ? t('documenten.vanWerkgever', { defaultValue: 'Opgesteld door je werkgever' }) : doc.bron === 'werkgever_upload' ? t('documenten.pdfArts', { defaultValue: 'PDF van de arbo-arts' }) : doc.professional_naam}
              {doc.werkgever_ondertekend_op && ` · ${t('documenten.werkgeverGetekend', { defaultValue: 'werkgever getekend {{datum}}', datum: datum(doc.werkgever_ondertekend_op) })}`}
            </p>
          </div>
          {statusChip}
        </div>

        {open ? (
          <div className={`mt-3 grid gap-4 ${teTekenen ? 'md:grid-cols-2' : ''}`}>
            {/* Links: het document */}
            <div>
              <p className="text-[11px] text-gray-500 font-mono break-all mb-1">SHA-256 {doc.document_hash}</p>
              {doc.heeft_bestand ? (
                <iframe title={doc.titel} src={documentBestandUrl(doc.id)} className="w-full h-[420px] rounded-lg border border-gray-200 bg-gray-50" />
              ) : (
                <div className="text-sm text-gray-800 whitespace-pre-line leading-relaxed max-h-[420px] overflow-y-auto rounded-lg border border-gray-100 bg-gray-50 p-3">{doc.inhoud}</div>
              )}
            </div>
            {/* Rechts: de akkoordverklaring */}
            {teTekenen && (
              <Ondertekenen
                doc={doc}
                onKlaar={() => {
                  setZojuist(true);
                  onGewijzigd();
                }}
              />
            )}
          </div>
        ) : (
          <button type="button" onClick={() => setOpen(true)} className="mt-3 w-full text-sm text-[#0B1F3A] font-medium py-1">
            {t('documenten.leesDocument', { defaultValue: 'Document lezen' })}
          </button>
        )}

        {wachtOpWerkgever && (
          <p className="mt-3 text-sm text-gray-700 flex items-center gap-1.5">
            <Clock className="w-4 h-4 text-gray-500" aria-hidden="true" />
            {t('documenten.wachtWerkgeverUitleg', { defaultValue: 'Je werkgever tekent eerst de akkoordverklaring. Daarna krijg je een SMS om zelf te ondertekenen.' })}
          </p>
        )}

        {verzegeld && (
          <div className="mt-3 rounded-lg border border-green-200 bg-green-50 p-3 space-y-2">
            <p className="text-sm text-green-800 flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4" aria-hidden="true" />
              {zojuist
                ? t('documenten.zojuistOndertekend', { defaultValue: 'Je hebt dit document zojuist rechtsgeldig ondertekend. Het dossier is cryptografisch afgesloten.' })
                : t('documenten.ondertekendOp', { defaultValue: 'Ondertekend op {{datum}} via SMS-akkoord (•••• {{suffix}})', datum: doc.ondertekend_op ? datum(doc.ondertekend_op) : '', suffix: doc.telefoon_suffix ?? '????' })}
            </p>
            {doc.zegel_hash && <p className="text-[11px] text-green-900 font-mono break-all">zegel {doc.zegel_hash}</p>}
            <Button size="sm" variant="outline" onClick={certificaat} disabled={downloadt}>
              {downloadt ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Award className="w-4 h-4 mr-1" />}
              {t('documenten.certificaat', { defaultValue: 'Download Audit-Certificaat (PDF)' })}
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

/** Poortwachter-documenten lezen en digitaal ondertekenen via SMS-akkoord (Digital Signing Wrapper). */
export const MedewerkerDocumentenPage: React.FC = () => {
  const { t } = useTranslation('medewerker');
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const focusId = params.get('doc');
  const docsQuery = useQuery({ queryKey: ['medewerker-documenten'], queryFn: getDocumenten });

  const vernieuw = () => {
    queryClient.invalidateQueries({ queryKey: ['medewerker-documenten'] });
    queryClient.invalidateQueries({ queryKey: ['medewerker-dashboard'] });
  };

  return (
    <div className="space-y-5">
      <Link to="/medewerker" className="inline-flex items-center gap-1 text-sm text-[#0B1F3A]">
        <ArrowLeft className="w-4 h-4" aria-hidden="true" />
        {t('documenten.terug', { defaultValue: 'Overzicht' })}
      </Link>

      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FileText className="w-6 h-6 text-[#0B1F3A]" aria-hidden="true" />
          {t('documenten.titel', { defaultValue: 'Poortwachter-documenten' })}
        </h1>
        <p className="text-sm text-gray-600 mt-0.5">
          {t('documenten.subtitel', { defaultValue: 'Wettelijke documenten uit je re-integratietraject, zoals de probleemanalyse en het plan van aanpak.' })}
        </p>
      </div>

      <div className="rounded-lg bg-blue-50 border border-blue-100 p-3 flex items-start gap-2 text-sm text-gray-700">
        <ShieldCheck className="w-5 h-5 text-[#0B1F3A] shrink-0" aria-hidden="true" />
        <p>
          {t('documenten.uitleg', {
            defaultValue:
              'Rechtsgeldig ondertekenen via SMS-2FA, direct in je browser. Je naam wordt nergens vastgelegd; alleen je pseudoniem, het tijdstip en de laatste 4 cijfers van je nummer komen op het Audit-Certificaat.',
          })}
        </p>
      </div>

      {docsQuery.isLoading && <p className="text-center text-gray-500 py-8">…</p>}
      {docsQuery.isError && (
        <p className="text-sm text-red-600" role="alert">{foutMelding(docsQuery.error, t('documenten.laadfout', { defaultValue: 'De documenten konden niet worden geladen.' }))}</p>
      )}
      {docsQuery.isSuccess && docsQuery.data.length === 0 && (
        <Card className="p-6 text-center"><p className="text-gray-700">{t('documenten.leeg', { defaultValue: 'Er zijn nog geen documenten voor je klaargezet.' })}</p></Card>
      )}
      {docsQuery.data?.map((doc) => (
        <DocumentKaart key={doc.id} doc={doc} onGewijzigd={vernieuw} focus={doc.id === focusId} />
      ))}
    </div>
  );
};

export default MedewerkerDocumentenPage;
